import { encode } from 'js-base64'
import { Observable, of } from 'rxjs'
import { mergeMap } from 'rxjs/operators'
import { ofType, StateObservable } from 'redux-observable'
import moment from 'moment'
import { EntityUpdateUnion, LogToServerActions, NegotiationSide, ValidationIssue } from 'api'
import { GlobalState } from 'store'
import { NotificationActions, NotificationType } from 'store/notification'
import { createAuthorisedUserSelector } from 'selectors/connectionSelectors'
import { ActionsUnion, createAction, getCurrentWindowId, splitString } from 'utils'
import { Locale, Translation } from 'locale'
import { IDiagnosticsLogger } from 'logger/diagnosticsLogger'
import { RepositoryEntry } from 'store/repository'
import { BTQAction } from 'epics'

export enum DiagnosticsActionTypes {
  SEND_DIAGNOSTICS = 'SEND_DIAGNOSTICS'
}

export interface DiagnosticsDependencies {
  diagnosticsLogger: IDiagnosticsLogger
}

export const DiagnosticsActions = {
  sendDiagnostics: () => createAction(DiagnosticsActionTypes.SEND_DIAGNOSTICS, { windowId: getCurrentWindowId() })
}

export type DiagnosticsActions = ActionsUnion<typeof DiagnosticsActions>

type EntityKeyInfo = {
  version: number | string | null
  subscriptionIdentifiers: string[]
  validationIssues: ValidationIssue[] | null
  availableEvents?: any[] | null
}

const getRepositoriesEntitiesKeyInfo = (entry: Record<string, RepositoryEntry<EntityUpdateUnion>>) => {
  return Object.values(entry).reduce<Record<string, EntityKeyInfo>>((acc, entityUpdate) => {
    const lastUpdate = entityUpdate.lastUpdate

    acc[entityUpdate.entityId] = {
      subscriptionIdentifiers: lastUpdate.subscriptionIdentifiers,
      version: 'version' in lastUpdate.entity ? lastUpdate.entity.version : null,
      validationIssues: 'validationIssues' in lastUpdate ? lastUpdate.validationIssues : null,
      availableEvents: 'availableEvents' in lastUpdate ? lastUpdate.availableEvents : null
    }
    return acc
  }, {})
}

const getImportantStoreData = (store: GlobalState) => {
  const { negotiationRepositoryStore, repositoryStore, systemStore, configurationStore, selectionStore } = store

  const { ORDER, DEAL, DEAL_HISTORY, DEAL_INTEREST_PERIOD, NEGOTIATION_HISTORY } = repositoryStore
  return {
    configurationStore,
    systemStore: {
      actionLog: systemStore.actionLog
    },
    selectionStore: {
      entitySubscriptions: selectionStore.entitySubscriptions
    },
    negotiationRepositoryStore: {
      initiator: getRepositoriesEntitiesKeyInfo(negotiationRepositoryStore[NegotiationSide.INITIATOR]),
      responder: getRepositoriesEntitiesKeyInfo(negotiationRepositoryStore[NegotiationSide.RESPONDER])
    },
    repositoryStore: {
      order: getRepositoriesEntitiesKeyInfo(ORDER),
      deal: getRepositoriesEntitiesKeyInfo(DEAL),
      dealHistory: getRepositoriesEntitiesKeyInfo(DEAL_HISTORY),
      dealInterestPeriod: getRepositoriesEntitiesKeyInfo(DEAL_INTEREST_PERIOD),
      negotiationHistory: getRepositoriesEntitiesKeyInfo(NEGOTIATION_HISTORY)
    }
  }
}

export function diagnosticsEpic(
  action$: Observable<BTQAction>,
  state$: StateObservable<GlobalState>,
  dependencies: DiagnosticsDependencies
): Observable<BTQAction> {
  return action$.pipe(
    ofType<BTQAction, DiagnosticsActionTypes.SEND_DIAGNOSTICS, DiagnosticsActions>(DiagnosticsActionTypes.SEND_DIAGNOSTICS),
    mergeMap(action => {
      const authorisedUser = createAuthorisedUserSelector()(state$.value)
      const currentTime = moment().format('YYYYMMDD_HHmmss')
      const importantStoreData = getImportantStoreData(state$.value)

      // Base64 encoding message as AWS cloudwatch logs escape JSON strings
      const encodedData = encode(JSON.stringify(importantStoreData))

      // Be mindful if changing the chunk length - cloudwatch will truncate messages
      const chunkedEncodedData = splitString(encodedData, 10000)
      const logStoreActions: BTQAction[] = chunkedEncodedData.map((storeChunk, i) => {
        const message = `Diagnostics Information Store Log - ${authorisedUser.username}_${currentTime} - ${i + 1} of ${
          chunkedEncodedData.length
        }`
        return LogToServerActions.logStore(message, storeChunk)
      })

      const diagnosticsLog = dependencies.diagnosticsLogger.getLog()
      let diagnosticsActions: BTQAction[] = []

      if (diagnosticsLog.length > 0) {
        diagnosticsActions = diagnosticsLog.map(logEntry => {
          return LogToServerActions.logMessage(logEntry.severity, `${logEntry.timestamp} ${logEntry.message}`)
        })

        dependencies.diagnosticsLogger.clearLog()
      }

      return of(
        ...logStoreActions,
        ...diagnosticsActions,
        NotificationActions.showGrowl({
          type: NotificationType.Success,
          messageText: Locale.translate(Translation.SC_DIAGNOSTICS_SENT),
          targetWindowId: action.payload.windowId
        })
      )
    })
  )
}
